/**
 * VINES (2026-10-05, Matt: "creeping wall vines to create character … on walls, climbing
 * columns, and crawling through trellises"; painting chosen over placed objects). The
 * vine roster: who each vine is, and the plain growth facts the generator reads
 * (bim-geometry categories/vine). The leaf cards, colors and sway are drawn by the viewer
 * from the same `id` (frontend plants/vineLayer.js), so the kernel and worker never
 * carry a leaf texture.
 *
 * Feet throughout. `leafFt` is one foliage CARD's size — a card is a CLUSTER of leaves
 * (the plant study's leaf textures), so its true leaves read at about a tenth of it; `offsetFt` how far the foliage
 * stands off the surface (creeping fig hugs it, wisteria hangs loose); `step` the growth
 * step; `branch` how readily stems fork; `leavesPerSqft` how many cards a mature planting carries per square foot of painted area.
 */
export interface VineSpecies {
  id: string
  name: string
  botanical: string
  /** the viewer's leaf card (plantLibrary LEAF keys) and flower card */
  leaf: 'small' | 'broad' | 'lance' | 'round'
  leafColor: string
  flower?: { color: string; share: number }
  leafFt: number
  offsetFt: number
  stemFt: number
  stemColor: string
  step: number
  branch: number
  leavesPerSqft: number
  /** how far foliage droops below the painted area (wisteria, bougainvillea) */
  droopFt: number
}

export const VINE_SPECIES: readonly VineSpecies[] = [
  {
    id: 'creepingFig', name: 'Creeping Fig', botanical: 'Ficus pumila',
    leaf: 'small', leafColor: '#3f6b2e', leafFt: 0.9, offsetFt: 0.05,
    stemFt: 0.007, stemColor: '#6b5a44', step: 0.35, branch: 0.55, leavesPerSqft: 5.5, droopFt: 0,
  },
  {
    id: 'bostonIvy', name: 'Boston Ivy', botanical: 'Parthenocissus tricuspidata',
    leaf: 'broad', leafColor: '#4f7a30', leafFt: 1.9, offsetFt: 0.12,
    stemFt: 0.018, stemColor: '#6a5440', step: 0.5, branch: 0.45, leavesPerSqft: 1.15, droopFt: 0,
  },
  {
    id: 'starJasmine', name: 'Star Jasmine', botanical: 'Trachelospermum jasminoides',
    leaf: 'lance', leafColor: '#2f4d26', flower: { color: '#f4f1e6', share: 0.22 },
    leafFt: 1.0, offsetFt: 0.15, stemFt: 0.016, stemColor: '#5d4d3a', step: 0.42, branch: 0.5, leavesPerSqft: 3.2, droopFt: 0.4,
  },
  {
    id: 'bougainvillea', name: 'Bougainvillea', botanical: "Bougainvillea 'Barbara Karst'",
    leaf: 'broad', leafColor: '#4c6a30', flower: { color: '#d8248a', share: 0.45 },
    leafFt: 1.4, offsetFt: 0.32, stemFt: 0.03, stemColor: '#6a5038', step: 0.6, branch: 0.4, leavesPerSqft: 1.8, droopFt: 1.5,
  },
  {
    id: 'wisteria', name: 'Wisteria', botanical: 'Wisteria sinensis',
    leaf: 'lance', leafColor: '#5f7f3a', flower: { color: '#9b7fd0', share: 0.35 },
    leafFt: 1.3, offsetFt: 0.38, stemFt: 0.05, stemColor: '#7a6a58', step: 0.7, branch: 0.35, leavesPerSqft: 1.6, droopFt: 2.5,
  },
]

const BY_ID = new Map(VINE_SPECIES.map((s) => [s.id, s]))

/** the species for an id, falling back to creeping fig (never null) */
export function vineSpeciesOf(id: unknown): VineSpecies {
  return BY_ID.get(String(id || '')) || VINE_SPECIES[0]
}

export const vineTypeName = (s: VineSpecies) => `Vine — ${s.name}`

/** one painted dab on the host, in the host's own surface coordinates (feet):
 *  walls: u = station along the wall path, v = height above the wall's base */
export type VineDab = [number, number, number]   // [u, v, radius]
